import { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import { DocxEditor } from "@eigenpal/docx-editor-react";

import { getToken } from "../../Utils/helper";
// eslint-disable-next-line import/no-unresolved
import "@eigenpal/docx-editor-react/styles.css";
import { api_without_cancellation } from "../../api/api";

import { handleVariableChange2 } from "./helpers";

const DOCX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function WPDiscoveryDraftBase({
  docId,
  updatedDocId,
  dynamicTemplateId,
  setPageId,
  setDropdownId,
  setDocName,
  setDoctData,
  state,
  dispatch,
  container,
  controls,
  type,
  showRightSidebar,
}) {
  const [isPropertyPaneReady, setIsPropertyPaneReady] = useState(false);

  const [dropdownPages, setDropdownPages] = useState([]);
  const [selectedPageOption, setSelectedPageOption] = useState(null);

  const [dropdownVariables, setDropdownVariables] = useState([]);

  const [renameDoc, setRenameDoc] = useState("");

  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";

  const saveTimeoutRef = useRef(null);
  const isDocumentReadyRef = useRef(false);
  const [buffer, setBuffer] = useState(null);

  const getDocument = useCallback(async () => {
    try {
      const response = await axios.get(
        `${origin}/api/getDocFile/?doc_id=${updatedDocId ? updatedDocId : docId}`,
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      if (response.status === 200) {
        setDoctData(response?.data);
        const fileResponse = await fetch(response?.data?.upload, { cache: "no-store" });
        if (!fileResponse.ok) {
          throw new Error("Unable to download document");
        }
        const arrayBuffer = await fileResponse.arrayBuffer();
        setDocName(response.data.file_name);
        setRenameDoc(response.data.file_name);
        setBuffer(arrayBuffer);
        dispatch({ type: "SET_LOADING", payload: false });
      }
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: true });
    }
  }, [dispatch, docId, origin, setDocName, setDoctData, updatedDocId]);

  const handlePageChange = (option) => {
    setSelectedPageOption(option);
    documentVariables(option.value);
  };

  const autoSaveContent = useCallback(() => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the save operation
    saveTimeoutRef.current = setTimeout(async () => {
      if (!container.current) {
        return;
      }
      if (!isDocumentReadyRef.current) {
        return;
      }

      try {
        const content = await Promise.race([
          container.current.save(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Save timeout")), 5000)),
        ]);

        if (!content || content.byteLength === 0) {
          return;
        }

        const formData = new FormData();
        formData.append("doc_id", docId);
        formData.append("file", new File([content], "autosave.docx", { type: DOCX_MIME_TYPE }));

        await api_without_cancellation.post(`${origin}/api/update-edit-doc/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } catch (error) {
        // A later edit will retry the debounced save.
      }
    }, 2000); // Wait 2 seconds after user stops typing
  }, [docId, origin]);

  const getTemplateData = async (options) => {
    try {
      const response = await api_without_cancellation.get(
        "/api/firmsetting-page/edit-dl-template/",
        {
          params: {
            template_id: dynamicTemplateId,
          },
        }
      );
      if (response.status === 200) {
        const page_id = response.data?.for_page?.id;
        const dropdown_id = response.data?.for_dropdown?.id;
        const temp_name =
          response.data?.for_template?.template_name ??
          response.data?.for_dynamic_letter_template?.for_template?.template_name;
        const doc_name =
          response.data?.for_template?.template?.file_name ??
          response.data?.for_dynamic_letter_template?.for_template?.template?.file_name;

        const copy_temp_name = temp_name + "-copy";

        setPageId(page_id);
        setDropdownId(dropdown_id);

        setDocName(doc_name);
        setRenameDoc(doc_name);

        dispatch({
          type: "SET_TEMP_NAME",
          payload: temp_name,
        });

        dispatch({ type: "SET_FILENAME", payload: copy_temp_name });
        dispatch({ type: "SET_UPDATED_FILENAME", payload: copy_temp_name });

        const optionSelect = options.find((option) => option.value === page_id);
        setSelectedPageOption(optionSelect);
        documentVariables(page_id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setIsPropertyPaneReady(true);
    documentPages();
  }, []);

  useEffect(() => {
    container.current?.getEditorRef?.()?.relayout?.();
  }, [showRightSidebar]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const documentPages = async () => {
    try {
      const response = await api_without_cancellation.get(
        `${origin}/api/firmsetting-page/get-document-variables-pages/`
      );

      if (response.status === 200) {
        const data = response.data;
        const optionPages = [
          {
            value: "all", // or another value of your choice
            label: "All",
          },
          ...data.map((item) => ({
            value: item.id, // map to `value`
            label: (
              <div style={{ display: "flex", alignItems: "center" }}>
                <img
                  src={item.page_icon}
                  alt="icon"
                  style={{ width: "20px", marginRight: "8px" }}
                />
                {item.name}
              </div>
            ),
          })),
        ];
        setDropdownPages(optionPages);
        getTemplateData(optionPages);
      }
    } catch {
      setDropdownPages([]);
    }
  };

  const documentVariables = async (page_id) => {
    try {
      const response = await api_without_cancellation.get(
        `${origin}/api/firmsetting-page/get-document-variables/?page_id=` + page_id
      );

      if (response.status === 200) {
        const data = response.data;
        const filteredData = data.filter((item) => item.button_name !== null);
        setDropdownVariables(filteredData);
      }
    } catch {
      setDropdownVariables([]);
    }
  };

  const renameDocFileName = async () => {
    try {
      const formData = new FormData();
      formData.append("doc_id", docId);
      formData.append("file_name", renameDoc);

      const response = await api_without_cancellation.post(
        `${origin}/api/rename-doc-wp/`,
        formData
      );
      if (response.status === 200) {
        if (response.data.success) {
          dispatch({
            type: "SHOW_MODAL",
            payload: `${renameDoc} renamed successfully!`,
          });
          setDocName(renameDoc);
        }
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  useEffect(() => {
    getDocument();
  }, [getDocument]);

  if (!docId || state.error) {
    return (
      <div className="doc-error">
        <p>Document not found!</p>
      </div>
    );
  }

  return (
    <>
      <div className="document-modal-body w-100 m-t-5">
        <div
          className={`d-flex flex-column justify-content-between m-0 ${showRightSidebar ? "discovery-draft-editor" : "discovery-draft-editor-withoutcontrols"}`}
          style={{
            opacity: state.loading ? 0 : 1,
            pointerEvents: state.loading ? "none" : "auto",
            width: showRightSidebar ? "calc(100% - 330px)" : "100%",
            height: "100% !important",
          }}
        >
          <>
            {buffer && (
              <DocxEditor
                ref={container}
                documentBuffer={buffer}
                mode="editing"
                documentName={state?.tempName}
                showToolbar
                showRuler={false}
                showZoomControl={false}
                showFileOpen={false}
                onChange={autoSaveContent}
                onFontsLoaded={() => {
                  isDocumentReadyRef.current = true;
                }}
                style={{
                  width: "100%",
                  height: "calc(100vh - 250px)",
                }}
              />
            )}
          </>
        </div>

        {showRightSidebar && <div style={{ height: "100%" }}>{controls ? controls : controls}</div>}

        <div>
          {type === "LetterTemplate" ? (
            <>
              <div className="row">
                <div className="col-sm-12">
                  <div>
                    <input
                      type="text"
                      className="form-control col"
                      value={renameDoc}
                      onChange={(e) => setRenameDoc(e.target.value)}
                    />
                  </div>
                  <div className="d-flex">
                    <button
                      type="button"
                      className="btn btn-primary fw-bold ms-auto m-t-5 m-b-5"
                      onClick={renameDocFileName}
                    >
                      Rename
                    </button>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  {isPropertyPaneReady && (
                    <Select
                      options={dropdownPages}
                      className="w-100"
                      onChange={handlePageChange}
                      value={selectedPageOption}
                      placeholder="Select a Page"
                      isSearchable={false}
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="row mt-4">
                {/* Column 1 (Odd-indexed items) */}
                <div className="col-sm-6 text-center">
                  <div className="icon-text-boxes">
                    {dropdownVariables
                      ?.filter((_, index) => index % 2 === 0)
                      .map((variable, index) =>
                        variable.button_name ? (
                          <div
                            key={index}
                            className="text-center height-25 btn-primary-lighter-2 font-weight-semibold  cursor-pointer m-t-5"
                            id="no-vertical-border"
                            onClick={() => handleVariableChange2(`{{${variable.name}}}`, container)}
                          >
                            <p className="name">{variable.button_name}</p>
                          </div>
                        ) : null
                      )}
                  </div>
                </div>

                {/* Column 2 (Even-indexed items) */}
                <div className="col-sm-6 text-center">
                  <div className="icon-text-boxes">
                    {dropdownVariables
                      ?.filter((_, index) => index % 2 !== 0)
                      .map((variable, index) =>
                        variable.button_name ? (
                          <div
                            key={index} // Unique key for second column
                            className="text-center height-25 btn-primary-lighter-2 font-weight-semibold cursor-pointer m-t-5"
                            id="no-vertical-border"
                            onClick={() => handleVariableChange2(`{{${variable.name}}}`, container)}
                          >
                            <p className="name">{variable.button_name}</p>
                          </div>
                        ) : null
                      )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default WPDiscoveryDraftBase;
